// @flow

import { type State } from '../../store/componentSettings/reducers';
import { type State as Pdb } from '../../store/pdb/types';
import {
  getDefaultDurationValue,
  getMileageAsNumber,
  getActivePdbId,
  getProductType,
  getKmPriceInEuros,
} from '../../store/pdb/selectors';
import {
  getContractPrefixText,
  getContractPostfixText,
  getContractDurationAbbrv,
  getMileageAbbrv,
  getAndAbbrv,
  getPerAbbrv,
  getYearAbbrv,
} from '../../store/componentSettings/selectors/carBlockSettings';
import { getHideMileageLabel, getMileageLabel } from '../../store/productTypeSettings';
import type { ProductTypeSettings } from '../../types';
import type { State as RouterState } from '../../store/router/types';
import priceFormat from '../priceFormat';
import roundTo from '../roundTo';

type GlobalState = {
  pdb: Pdb,
  componentSettings: State,
  router: RouterState,
  productTypeSettings: ProductTypeSettings,
};

type RemarkText = string;

type Defaults = {
  mileage?: number,
};

export const buildRemarkTextWithDefaults = (defaults?: Defaults = {}) => (
  state: GlobalState,
  pdbId?: string,
): RemarkText => {
  const selectedPdbId = pdbId || getActivePdbId(state);
  const hideMileageLabel = getHideMileageLabel(state, selectedPdbId);
  const mileageLabel = getMileageLabel(state, selectedPdbId);

  const productType = getProductType(state, selectedPdbId);
  const contractPrefix = getContractPrefixText(state) || '';
  const contractPostfix = getContractPostfixText(state) || '';
  const duration = getDefaultDurationValue(state, selectedPdbId) || '';
  const contractDurationAbbrv = getContractDurationAbbrv(state) || '';

  if (hideMileageLabel) {
    // If the 'hide mileage label' checkbox in the CMS is checked, return the duration but not the
    // mileage
    return `${contractPrefix} ${duration} ${contractDurationAbbrv}`;
  } if (mileageLabel) {
    // If the 'hide mileage label' checkbox in the CMS is not checked and a custom mileage label is
    // entered, replace the default mileage text
    return `${contractPrefix} ${duration} ${contractDurationAbbrv} ${mileageLabel}`;
  }

  const mileage = defaults.mileage || getMileageAsNumber(state, selectedPdbId) || '';
  const mileageAbbrv = getMileageAbbrv(state) || '';
  const andAbbrv = getAndAbbrv(state) || '';
  const perAbbrv = getPerAbbrv(state) || '';
  const yearAbbrv = getYearAbbrv(state) || '';
  const kmPrice = priceFormat(getKmPriceInEuros(state, selectedPdbId), {
    decimalDash: false,
    currency: true,
  }) || '';

  // convert mileage into mileage per year with '.' - and if mileage < 1000, round to 1000nds
  const mileagePerYear = (roundTo(parseInt(mileage, 10) * 12)).toLocaleString('nl');

  switch (productType) {
    case 'Justdrive':
      // Op basis van | 60 | mnd | en | € 0.20 | per | km | [ incl. wegenbelasting ]
      return `${contractPrefix} ${duration} ${contractDurationAbbrv} ${andAbbrv} ${kmPrice} ${perAbbrv} ${mileageAbbrv} ${contractPostfix}`;
    default:
      // Op basis van | 60 | mnd | en | 12.000 | km | / | jr | [ incl. wegenbelasting ]
      return `${contractPrefix} ${duration} ${contractDurationAbbrv} ${andAbbrv} ${mileagePerYear} ${mileageAbbrv}/${yearAbbrv} ${contractPostfix}`;
  }
};

// export a simple builder without defaults
export const buildRemarkText = buildRemarkTextWithDefaults();

export default null;
